// Laufende Stempeluhr für die ganze App: das Pillen-Widget und der Zeit-Bereich
// zeigen denselben Zustand, und die Uhr läuft sekundengenau weiter, ohne dass die
// Seite neu geladen wird.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ausstempeln as apiAusstempeln, einstempeln as apiEinstempeln, ladeModelle, ladeStempel,
  ladeZuordnungen, offenerStempel, pauseBeenden, pauseStarten,
} from '../lib/zeiterfassung/api';
import {
  dauerMinuten, gesamtsaldo, nettoMinuten, tagVon, tageszeile,
  type Arbeitszeitmodell, type Stempel, type Zuordnung,
} from '../lib/zeiterfassung/berechnung';
import { wochenTage } from '../lib/island/zeitApp';

interface ZeitContextValue {
  meineId: string | null;
  laufend: Stempel | null;
  /** Läuft gerade eine Pause? */
  inPause: boolean;
  /** Sekundengenaue Uhr — treibt die Anzeige der laufenden Dauer. */
  jetzt: Date;
  /** Netto-Minuten des laufenden Abschnitts. */
  laufendeMinuten: number;
  /** Ist-Minuten des heutigen Tages inklusive laufendem Abschnitt. */
  heuteMinuten: number;
  heuteSollMinuten: number;
  /** Gesamtsaldo bis heute (Über-/Minusstunden). */
  saldoMinuten: number;
  /** Stempel des heutigen Tages (Tagesereignisse der Pille). */
  heuteStempel: Stempel[];
  /** Montag–Sonntag dieser Woche: Ist/Soll je Tag (Wochenbalken der Pille). */
  woche: { tag: string; istMinuten: number; sollMinuten: number }[];
  bereit: boolean;
  /** Liefern `false`, wenn das Speichern scheiterte (die Pille zeigt dann einen Hinweis). */
  starten: () => Promise<boolean>;
  beenden: () => Promise<boolean>;
  pauseUmschalten: () => Promise<boolean>;
  neuLaden: () => Promise<void>;
}

const LEER: ZeitContextValue = {
  meineId: null, laufend: null, inPause: false, jetzt: new Date(), laufendeMinuten: 0,
  heuteMinuten: 0, heuteSollMinuten: 0, saldoMinuten: 0, heuteStempel: [], woche: [], bereit: false,
  starten: async () => false, beenden: async () => false, pauseUmschalten: async () => false, neuLaden: async () => {},
};

const ZeitContext = createContext<ZeitContextValue | null>(null);
export const useZeit = (): ZeitContextValue => useContext(ZeitContext) ?? LEER;

/** Erster Tag des Vormonats — so weit zurück reicht der Saldo-Abruf des Widgets. */
function saldoStart(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1, 1);
  return tagVon(d.toISOString());
}

export const ZeitProvider: React.FC<{ userId: string | null; children: React.ReactNode }> = ({ userId, children }) => {
  const [laufend, setLaufend] = useState<Stempel | null>(null);
  const [stempel, setStempel] = useState<Stempel[]>([]);
  const [modelle, setModelle] = useState<Arbeitszeitmodell[]>([]);
  const [zuordnungen, setZuordnungen] = useState<Zuordnung[]>([]);
  const [bereit, setBereit] = useState(false);
  const [jetzt, setJetzt] = useState(() => new Date());

  // Sekundentakt nur, solange etwas läuft — sonst reicht der Minutentakt.
  useEffect(() => {
    const takt = laufend ? 1000 : 60000;
    const t = setInterval(() => setJetzt(new Date()), takt);
    return () => clearInterval(t);
  }, [laufend]);

  const neuLaden = useCallback(async () => {
    if (!userId) { setLaufend(null); setStempel([]); setBereit(true); return; }
    const heute = tagVon(new Date().toISOString());
    const [offen, liste, m, z] = await Promise.all([
      offenerStempel(userId),
      ladeStempel(userId, saldoStart(), heute),
      ladeModelle(),
      ladeZuordnungen(userId),
    ]);
    setLaufend(offen);
    setStempel(liste);
    setModelle(m);
    setZuordnungen(z);
    setBereit(true);
  }, [userId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  const starten = useCallback(async () => {
    if (!userId) return false;
    if (laufend) return true;
    const neu = await apiEinstempeln(userId);
    if (!neu) return false;
    setLaufend(neu);
    setStempel((prev) => [...prev, neu]);
    return true;
  }, [userId, laufend]);

  const beenden = useCallback(async () => {
    if (!laufend) return true;
    const ok = await apiAusstempeln(laufend);
    if (ok) await neuLaden();
    return ok;
  }, [laufend, neuLaden]);

  const inPause = !!laufend?.pausen?.some((p) => !p.ende);

  const pauseUmschalten = useCallback(async () => {
    if (!laufend) return true;
    const ok = inPause ? await pauseBeenden(laufend.id) : await pauseStarten(laufend.id);
    if (ok) await neuLaden();
    return ok;
  }, [laufend, inPause, neuLaden]);

  const heute = tagVon(jetzt.toISOString());
  // Der laufende Abschnitt steckt in `stempel`, ist dort aber ohne Ende gespeichert —
  // die Berechnung rechnet ihn gegen `jetzt`, deshalb tickt die Anzeige mit.
  const heuteZeile = useMemo(
    () => tageszeile(heute, stempel, zuordnungen, modelle, jetzt),
    [heute, stempel, zuordnungen, modelle, jetzt],
  );
  // Wochenbalken brauchen keine Sekunden – nur einmal je Minute neu rechnen,
  // sonst liefe die Woche bei laufender Uhr sekündlich durch tageszeile().
  const minute = Math.floor(jetzt.getTime() / 60000);
  const woche = useMemo(() => {
    const stand = new Date(minute * 60000);
    return wochenTage(stand).map((tag) => {
      const z = tageszeile(tag, stempel, zuordnungen, modelle, stand);
      return { tag, istMinuten: z.istMinuten, sollMinuten: z.sollMinuten };
    });
  }, [stempel, zuordnungen, modelle, minute]);
  const saldoMinuten = useMemo(
    () => gesamtsaldo(stempel, zuordnungen, modelle, heute, jetzt),
    [stempel, zuordnungen, modelle, heute, jetzt],
  );

  const wert = useMemo<ZeitContextValue>(() => ({
    meineId: userId,
    laufend,
    inPause,
    jetzt,
    laufendeMinuten: laufend ? nettoMinuten({ ...laufend }, jetzt) : 0,
    heuteMinuten: heuteZeile.istMinuten,
    heuteSollMinuten: heuteZeile.sollMinuten,
    saldoMinuten,
    heuteStempel: heuteZeile.stempel,
    woche,
    bereit,
    starten, beenden, pauseUmschalten, neuLaden,
  }), [userId, laufend, inPause, jetzt, heuteZeile, saldoMinuten, woche, bereit, starten, beenden, pauseUmschalten, neuLaden]);

  return <ZeitContext.Provider value={wert}>{children}</ZeitContext.Provider>;
};

/** Reine Anzeige-Hilfe für das Widget: „seit 08:12" bzw. „Pause seit 12:30". */
export function laufendSeit(s: Stempel | null, inPause: boolean): string {
  if (!s) return '';
  if (inPause) {
    const p = (s.pausen || []).find((x) => !x.ende);
    if (p) return `Pause seit ${new Date(p.beginn).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return `seit ${new Date(s.beginn).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

export { dauerMinuten };
